import { generateShortNameAndBrand } from 'actions'
import { SecondaryRegularButton } from 'components/ReusableComponents/Button/RegularButtons'
import ConfirmationDialog from 'components/ReusableComponents/ConfirmationDialog'
import CustomHeader from 'components/ReusableComponents/HeaderTitles'
import { ReactComponent as ExcelIcon } from 'components/ReusableComponents/Icons/svg/excel.svg'
import LoadingIndicator from 'components/ReusableComponents/LoadingIndicator'
import Table, { TitleCell, TitleRow } from 'components/ReusableComponents/Table'
import EmailViewerDialog from 'features/EmailLogs/EmailViewerDialog'
import EmailRow from 'features/EmailLogs/Row'
import { ReactComponent as ClickedIcon } from 'features/EmailLogs/Row/EventsCell/svgs/clicked.svg'
import { ReactComponent as DeliveredIcon } from 'features/EmailLogs/Row/EventsCell/svgs/delivered.svg'
import { ReactComponent as OpenedIcon } from 'features/EmailLogs/Row/EventsCell/svgs/opened.svg'
import { useInternalAdmin } from 'hooks'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import billingApi from 'services/api/billing'
import { formatPrice } from 'utils/common'
import downloadAsCsv from 'utils/downloadAsCsv'

import styles from './ROI.module.scss'

const Cell = ({ months, invoiceDate, subsidiary }) => {
  const startOfMonth = moment(invoiceDate).startOf('month').format('YYYY-MM-DD')

  const invoice = months
    .find(month => month.invoiceDate === invoiceDate)
    .invoices.find(invoice => invoice.subsidiaryId === subsidiary.id)

  const goodInvoiceStatuses = [
    'Paid',
    'PaidPartially',
    'ReviewCompleted',
    'PaymentPending',
    'PaymentFailed'
  ]

  const inProgressStatuses = ['MonthInProgress', 'MonthSkipped']

  const unknownStatuses = ['Unknown']

  if (inProgressStatuses.includes(invoice.invoiceStatus)) {
    return (
      <tr>
        <td>-</td>
      </tr>
    )
  }

  if (unknownStatuses.includes(invoice.invoiceStatus)) {
    return (
      <tr>
        <td>-</td>
      </tr>
    )
  }

  if (goodInvoiceStatuses.includes(invoice.invoiceStatus)) {
    return (
      <tr>
        <td>
          {invoice.isDisconnected ? (
            '-'
          ) : (
            <div>
              <Link
                to={`/s/${invoice.subsidiaryId}/roi/invoiced-bookings/${invoice.invoiceId}/${startOfMonth}`}
                className={styles.invoiceControls}
              >
                <div className={styles.invoiceAmount}>{formatPrice(invoice.revenue)}</div>
                <span to="/ROI" className={styles.invoiceLink}>
                  View bookings →
                </span>
              </Link>
            </div>
          )}
        </td>
        <td>
          <div className={styles.invoiceAmount}>{formatPrice(invoice.invoice)}</div>
        </td>
      </tr>
    )
  }

  return (
    <tr>
      <td>-</td>
    </tr>
  )
}

const ROI = () => {
  const [months, setMonths] = useState()
  const [subsidiaries, setSubsidiaries] = useState()
  const [isLoading, setIsLoading] = useState(true)
  const [isErred, setIsErred] = useState(false)

  const [visibleInvoices, setVisibleInvoices] = useState()
  const [invoiceDates, setInvoiceDates] = useState()
  const [maxAvailableDate, setMaxAvailableDate] = useState()
  const [minAvailableDate, setMinAvailableDate] = useState()

  const [groupName, setGroupName] = useState('')

  const firstMonthIndex = months?.findIndex(
    month => month.invoiceDate === visibleInvoices[0]
  )

  const [emails, setEmails] = useState()
  const [isEmailDialogVisibleForMonth, setEmailDialogVisibleForMonth] = useState()

  const [emailViewId, setEmailViewId] = useState()

  const isInternalAdmin = useInternalAdmin()

  const isIndependentlyManaged = useSelector(s => s.hotelDetails.groupId) === -100

  const isDemoMode = useSelector(s => s.login.isDemoMode)

  const getHotelDisplayName = (subsidiary, index) => {
    if (isDemoMode) {
      return `Hotel ${index + 1}`
    }
    return subsidiary.shortName
  }

  useEffect(() => {
    if (isInternalAdmin) {
      billingApi
        .roiEmails()
        .then(res => {
          setEmails(res.months)
        })
        .catch(err => {
          console.warn(err)
        })
    }
  }, [isInternalAdmin])

  useEffect(() => {
    billingApi
      .roi()
      .then(res => {
        setSubsidiaries(
          res.subsidiaries.map(s => ({
            ...s,
            ...generateShortNameAndBrand(s.name)
          }))
        )

        setVisibleInvoices(res.months.slice(-3).map(i => i.invoiceDate))
        setInvoiceDates(res.months.map(i => i.invoiceDate))
        setMaxAvailableDate(res.months[res.months.length - 1].invoiceDate)
        setMinAvailableDate(res.months[0].invoiceDate)
        setGroupName(isIndependentlyManaged ? '' : res.merchantName)

        setMonths(res.months)
        setIsErred(false)
      })
      .catch(() => {
        setIsErred(true)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [isIndependentlyManaged])

  function handleClickDownload() {
    const createTitles = () => {
      const titles = ['']
      invoiceDates.forEach(date => {
        const formattedDate = moment(date).format('MMMM YYYY')
        titles.push(`${formattedDate} Revenue`)
        titles.push(`${formattedDate} Invoice Amount`)
      })
      return titles
    }

    const titleRow = createTitles()

    const resAsArrays = subsidiaries.map((subsidiary, index) => {
      const row = [subsidiary.shortName]
      months.forEach(month => {
        row.push(month.invoices[index].revenue)
        row.push(month.invoices[index].invoice)
      })
      return row
    })

    const dataArray = [titleRow, ...resAsArrays]
    const fileName = `roi-report-${moment().format('MMMM YYYY')}.csv`

    downloadAsCsv(dataArray, fileName)
  }

  return (
    <>
      {emailViewId && (
        <EmailViewerDialog
          emailId={emailViewId}
          handleClose={() => setEmailViewId(null)}
        />
      )}
      {isEmailDialogVisibleForMonth && (
        <ConfirmationDialog
          title={`Sent emails for ${moment(isEmailDialogVisibleForMonth).format(
            'MMMM YYYY'
          )}`}
          onClick={() => setEmailDialogVisibleForMonth(null)}
          theme="blacklist"
          maxWidth={1200}
          //allowOverflow
          onClose={() => setEmailDialogVisibleForMonth(null)}
        >
          <div style={{ overflowX: 'hidden', overflowY: 'auto' }}></div>
          <div className={styles.emailTable}>
            <Table widths={[280, 200, 400, 224, 30]}>
              <TitleRow style={{ position: 'sticky', top: 0 }}>
                <TitleCell>To</TitleCell>
                <TitleCell>Send on</TitleCell>
                <TitleCell>Subject</TitleCell>
                <TitleCell>Events</TitleCell>
                <TitleCell>View</TitleCell>
              </TitleRow>
              {emails
                .find(e => e.invoiceDate === isEmailDialogVisibleForMonth)
                .emailDeliveries.map(m => (
                  <EmailRow {...m} key={m.id} handleSetViewId={setEmailViewId} />
                ))}
            </Table>
          </div>
        </ConfirmationDialog>
      )}
      <CustomHeader
        title="Return on Investment"
        subtitle={isDemoMode ? 'Hotel Group' : groupName ? `For ${groupName}` : null}
        width={1070}
      >
        {!isErred && !isLoading && (
          <SecondaryRegularButton
            className={styles.copyToExcelButton}
            onClick={() => handleClickDownload()}
          >
            <ExcelIcon /> Download as CSV
          </SecondaryRegularButton>
        )}
      </CustomHeader>
      <div className={styles.tableHolder}>
        {isLoading && <LoadingIndicator />}
        {isErred && (
          <div className={styles.noResults}>
            ROI data is currently unavailable. Please check back later.
          </div>
        )}
        {!isLoading && !isErred && (
          <table className={styles.table}>
            <thead>
              <tr className={styles.months}>
                <th style={{ border: 'none' }}></th>
                {visibleInvoices.map(i => (
                  <th className={styles.monthTh} colSpan={2}>
                    {moment(i).format('MMMM YYYY')}
                    {emails &&
                      emails.find(e => e.invoiceDate === i)?.emailDeliveries?.length >
                        0 && (
                        <button onClick={() => setEmailDialogVisibleForMonth(i)}>
                          <DeliveredIcon style={{ marginTop: -1 }} />
                          {emails.find(e => e.invoiceDate === i)?.emailDeliveries.length}
                          <OpenedIcon fill="#A8ADB2" />
                          {emails.find(e => e.invoiceDate === i).totalOpen}
                          <ClickedIcon fill="#A8ADB2" />
                          {emails.find(e => e.invoiceDate === i).totalClick}
                        </button>
                      )}
                  </th>
                ))}
              </tr>
              <tr className={styles.titles}>
                <th className={styles.controlsTh}>
                  <div className={styles.controls}>
                    {!visibleInvoices.includes(minAvailableDate) && (
                      <button
                        onClick={() =>
                          months[firstMonthIndex - 3]
                            ? setVisibleInvoices(
                                months
                                  .slice(firstMonthIndex - 3, firstMonthIndex)
                                  .map(i => i.invoiceDate)
                              )
                            : setVisibleInvoices(
                                months.slice(0, 3).map(i => i.invoiceDate)
                              )
                        }
                      >
                        <svg width={18} height={18}>
                          <path d="M10.5 4.5l1.057 1.058L8.123 9l3.434 3.443L10.5 13.5 6 9l4.5-4.5z" />
                        </svg>
                        previous month
                      </button>
                    )}
                    {!visibleInvoices.includes(maxAvailableDate) && (
                      <button
                        className={styles.nextMonthButton}
                        onClick={() =>
                          setVisibleInvoices(
                            months
                              .slice(firstMonthIndex + 3, firstMonthIndex + 6)
                              .map(i => i.invoiceDate)
                          )
                        }
                      >
                        next month
                        <svg width="18" height="18">
                          <path d="M7.5 13.5L6.4425 12.4425L9.8775 9L6.4425 5.5575L7.5 4.5L12 9L7.5 13.5Z" />
                        </svg>
                      </button>
                    )}
                  </div>
                </th>
                {visibleInvoices.map(i => (
                  <td colSpan={2} key={i}>
                    <table>
                      <tbody>
                        <tr>
                          <td>Revenue</td>
                          <td>Invoice amount</td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                ))}
              </tr>
              <tr className={styles.totalRow}>
                <th>Total ({subsidiaries.length} hotels)</th>
                {visibleInvoices.map(invoiceDate => (
                  <td colSpan={2} key={invoiceDate}>
                    <table>
                      <tbody>
                        <tr>
                          <td className={styles.revenue}>
                            {formatPrice(
                              months.find(month => month.invoiceDate === invoiceDate)
                                ?.total?.revenue
                            )}
                          </td>
                          <td className={styles.invoice}>
                            {formatPrice(
                              months.find(month => month.invoiceDate === invoiceDate)
                                ?.total?.invoice
                            )}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                ))}
              </tr>
            </thead>
            <tbody className={styles.tableBody}>
              {subsidiaries
                .filter(subsidiary =>
                  groupName === 'Highgate' ? !subsidiary.name.includes('Hyatt') : true
                )
                .map((subsidiary, i) => (
                  <tr>
                    <th className={styles.hotelCell}>
                      {!isDemoMode && (
                        <p className={styles.hotelBrand}>{subsidiary.brand}</p>
                      )}
                      <p className={styles.shortHotelName}>
                        {getHotelDisplayName(subsidiary, i)}
                      </p>
                    </th>
                    {visibleInvoices.map(invoiceDate => (
                      <td colSpan={2}>
                        <table>
                          <tbody>
                            <Cell
                              months={months}
                              invoiceDate={invoiceDate}
                              subsidiary={subsidiary}
                            />
                          </tbody>
                        </table>
                      </td>
                    ))}
                  </tr>
                ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  )
}

export default ROI
